import { useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { FaIcon, NgButton } from '@/ui';
import { copyToClipboard } from '@/lib/browser/clipboard';
import { buildDiagnosticReport } from '@/lib/diagnostics';
import { dismissUnhandledError, getUnhandledError, subscribeToUnhandledErrors } from '@/lib/errors/unhandledErrors';
import type { UnhandledError } from '@/lib/errors/unhandledErrors';

type CopyAttempt = {
  error: UnhandledError;
  succeeded: boolean;
};

export function UnhandledErrorBar() {
  const { t } = useTranslation();
  const error = useSyncExternalStore(subscribeToUnhandledErrors, getUnhandledError);
  const [attempt, setAttempt] = useState<CopyAttempt | null>(null);

  if (error === null) {
    return null;
  }

  const copyError = async () => {
    const report = buildDiagnosticReport({
      error: error.detail,
      errorOccurredAt: error.occurredAt,
      errorPageUrl: error.pageUrl,
    });

    setAttempt({ error, succeeded: await copyToClipboard(report) });
  };

  const attemptOnCurrentError = attempt?.error === error ? attempt : null;

  return (
    <div className="unhandled-error-bar" role="alert">
      <div className="d-flex gap-3 align-items-start">
        <div className="d-flex flex-column flex-lg-row gap-2 align-items-lg-center flex-grow-1">
          <div className="flex-grow-1">{t('Blazor_UnhandledError_Info')}</div>

          <NgButton
            icon="fal fa-rotate"
            text={t('Blazor_UnhandledError_Refresh')}
            onClick={() => window.location.reload()}
          />

          <NgButton
            icon={attemptOnCurrentError?.succeeded === false ? 'fal fa-warning' : 'fal fa-copy'}
            text={
              attemptOnCurrentError === null
                ? t('Blazor_UnhandledError_Copy')
                : attemptOnCurrentError.succeeded
                  ? t('Tooltip_CopiedToClipboard')
                  : t('CopyButtonFailureMessage')
            }
            onClick={copyError}
          />
        </div>

        <button
          type="button"
          className="unhandled-error-bar__dismiss btn btn-link p-0"
          aria-label={t('Button_Close')}
          onClick={dismissUnhandledError}
        >
          <FaIcon icon="fal fa-xmark" />
        </button>
      </div>
    </div>
  );
}
